import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { RequestHistoryComponent } from '@components/request-history/request-history.component';
import { ColorAliases, GridOverrides, RowSelection } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  ButtonToggleComponent,
  GridCellFileUploadComponent,
  GridCellWithButtonComponent,
  GridHeaderGroupComponent,
  GridHeaderWithButtonComponent,
  GridModule,
  GridSelectRendererComponent
} from '@mds/ui';
import { CampaignTabsEnum } from '@pages/data-collection/helpers/data-collection-tabs';
import { QUESTIONNAIRE_IPC_FILE_TYPE } from '@pages/information-requests/information-request';
import { InformationRequestService } from '@pages/information-requests/information-request.service';
import { ComplianceQuestionnaireBulkStatusComponent } from '@pages/information-requests/request/compliance-questionnaire/compliance-questionnaire-bulk-status/compliance-questionnaire-bulk-status.component';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDataService } from '@services/global-data.service';
import { ThreadApiService } from '@services/thread-api.service';
import { MenuService } from '@services/menu.service';
import { ToastrService } from '@services/toaster.service';
import { ICellRendererParams } from 'ag-grid-community';
import { GetRowIdParams, GridApi } from 'ag-grid-enterprise';
import { finalize, forkJoin, of, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { ComplianceQuestionnaireEditComponent } from './compliance-questionnaire-edit/compliance-questionnaire-edit.component';
import { ComplianceQuestionnaireStatusComponent } from './compliance-questionnaire-status/compliance-questionnaire-status.component';

@Component({
  selector: 'app-compliance-questionnaire',
  templateUrl: './compliance-questionnaire.component.html',
  styleUrls: ['./compliance-questionnaire.component.scss'],
  standalone: true,
  imports: [MatIconModule, GridModule, ButtonToggleComponent, MdsIconsModule, RequestHistoryComponent],
  providers: [InformationRequestService]
})
export class ComplianceQuestionnaireComponent implements OnInit, OnChanges {
  @Input() response;
  @Input() threads;
  @Input() rid: string;
  @Input() errors: { message: string; data: string | null }[] = [];

  public readonly mdsIcon = MdsIcon;
  public readonly tertiaryColor = [ColorAliases.TERTIARY];

  private invalidProductNames = new Set<string>();

  showLess = false;
  gridOverrides: GridOverrides;
  gridApi: GridApi;
  editable: boolean;
  userInfo = JSON.parse(localStorage.getItem('user_info'));
  isUserExternalSupplier = (this.userInfo?.features ?? [])
    .map((feature: { plan: string }) => feature.plan)
    .includes('external_supplier');
  reviewMode = false;
  @Output() gridReady: EventEmitter<GridApi> = new EventEmitter<GridApi>();
  private destroy$ = new Subject<void>();
  private menuService = inject(MenuService);
  private readonly router = inject(Router);
  comment = '';
  groupColumnVisible = false;
  groupLabelMap = new Map<string, string>();
  isBulkReview = false;
  filteredRowSelection$ = new Subject<any[]>();
  tabs = [
    { label: 'Questionnaire', value: CampaignTabsEnum.QUESTIONNAIRES, icon: MdsIcon.CAMPAIGNS },
    { label: 'History', value: CampaignTabsEnum.HISTORY, icon: MdsIcon.HISTORY }
  ];
  selectedTab: CampaignTabsEnum = CampaignTabsEnum.QUESTIONNAIRES;
  campaignTabs = CampaignTabsEnum;

  constructor(
    private dialog: MatDialog,
    private informationRequestService: InformationRequestService,
    private toastrService: ToastrService,
    private eventDelegatorService: EventDelegatorService,
    private globalDataService: GlobalDataService,
    private threadApiService: ThreadApiService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.errors) {
      this.invalidProductNames = this.parseInvalidProductNames(this.errors);
      this.gridApi?.redrawRows();
    }
  }

  ngOnInit(): void {
    if (this.response) {
      this.groupColumnVisible = this.shouldShowGroupColumn(this.response?.tabs[0]?.lines);
    }
    this.createTableOverrides();
  }

  private parseInvalidProductNames(errors: { message: string; data: string | null }[]): Set<string> {
    const names = new Set<string>();
    errors.forEach((error) => {
      const matches: string[] = error.message.match(/'([^']+)'/g) ?? [];
      matches.forEach((match) => names.add(match.slice(1, -1)));
    });
    return names;
  }

  generateGroupLabels(rowData: any[]) {
    rowData.forEach((row) => {
      const groupKey = row.submission_id;
      if (groupKey && !this.groupLabelMap.has(groupKey)) {
        this.groupLabelMap.set(groupKey, row.statement);
      }
    });
  }

  shouldShowGroupColumn(lines: any[]): boolean {
    const hasSubmissionId = lines.some((productLines) =>
      productLines.some((line) => line.key.includes('out_l_declaration_') && line?.declaration?.submission_id)
    );
    return hasSubmissionId;
  }

  onStatusUpdate(params) {
    this.updateGridRowStatus(params, params.node.data.status);
    this.checkAcceptButton();
  }

  checkAcceptButton() {
    if (this.gridApi) {
      let allStatusUpdated = true;
      this.gridApi.forEachNode((node) => {
        if (!node?.data?.status && !node.group) {
          allStatusUpdated = false;
        }
      });
      this.menuService.updateMenuItem('close', { visible: allStatusUpdated });
    }
  }

  async onReview(gridParams, editable) {
    const selectedFilteredRows = await this.getFilterDataRows();
    if (!selectedFilteredRows.length) return;
    const selectedProducts = [];
    const selectedRslKey = gridParams?.column?.colId ?? '';
    const selectedRslRid = gridParams?.colDef?.headerComponentParams?.rid || gridParams.rid;
    const selectedRsl = this.response.additional_data.rsls.find((rsl) => rsl.rid === selectedRslRid);
    const selectedAttachments = [];
    const selectedSkuExemptions = [];
    let selectedDeclaration = null;

    selectedFilteredRows.forEach((row) => {
      const { attachments, sku_exemptions, declaration } = row.lines.find((line) => line.key === selectedRslKey);
      if (selectedFilteredRows.length > 1 && row.locked) return;

      if (!declaration?.locked) {
        selectedAttachments.push(...attachments);
        selectedSkuExemptions.push(...sku_exemptions);
        selectedProducts.push(row);
        selectedDeclaration = declaration;
      }
    });

    if (selectedProducts.length < 1) return;
    let thread = null;
    if (selectedProducts.length === 1) {
      thread = this.getThread(selectedProducts[0].rid, selectedRslRid);
    }
    const dialogRef = this.dialog.open(ComplianceQuestionnaireStatusComponent, {
      panelClass: 'dialog-panel',
      disableClose: true,
      closeOnNavigation: false,
      width: '800px',
      autoFocus: false,
      data: {
        editable,
        selectedRsl: { key: selectedRslKey, ...selectedRsl },
        selectedProducts,
        attachments: selectedAttachments ?? [],
        sku_exemptions: selectedSkuExemptions ?? [],
        declaration: selectedDeclaration ?? null,
        actions: this.response.tabs[0].actions,
        thread
      }
    });
    const instance = dialogRef.componentInstance;
    instance.submitDataEmitter.subscribe((response) => {
      dialogRef?.close();
      const skuRids = selectedProducts.map((product) => product.rid.replace('#', ''));
      const comment = response.commentSubmitted ? undefined : response.comment;
      this.submitStatus(this.buildStatusPayload(skuRids, response.status, comment));
      this.reload();
    });
  }

  async onBulkReview() {
    const selectedFilteredRows = await this.getFilterDataRows();
    if (selectedFilteredRows.length < 2) return;
    const dialogRef = this.dialog.open(ComplianceQuestionnaireBulkStatusComponent, {
      panelClass: 'dialog-panel',
      disableClose: true,
      closeOnNavigation: false,
      width: '500px',
      autoFocus: false,
      data: {
        productCount: selectedFilteredRows.length
      }
    });
    const instance = dialogRef.componentInstance;
    instance.submitDataEmitter.subscribe((response) => {
      this.isBulkReview = true;
      dialogRef?.close();
      const selectedNodes = this.gridApi.getSelectedNodes();
      const ids = [];
      selectedNodes.forEach((node: any) => {
        if (!node.group && node.displayed) {
          ids.push(node.data.rid.replace('#', ''));
          node.setDataValue('status', response.status);
        }
      });
      this.submitStatus(this.buildStatusPayload(ids, response.status, response.comment));
      this.checkAcceptButton();
    });
  }

  async onEdit(gridParams, editable, isBulkEdit = false) {
    const selectedFilteredRow = await this.getFilterDataRows();
    if (!selectedFilteredRow.length) return;
    if (isBulkEdit && selectedFilteredRow.length < 2) return;
    const selectedProducts = [];
    const selectedRslKey = gridParams?.column?.colId ?? '';
    const selectedRslRid = gridParams?.colDef?.headerComponentParams?.rid || gridParams.rid;
    const selectedRsl = this.response.additional_data.rsls.find((rsl) => rsl.rid === selectedRslRid);
    const selectedAttachments = [];
    const selectedSkuExemptions = [];
    let selectedDeclaration = null;

    selectedFilteredRow.forEach((row) => {
      const { attachments, sku_exemptions, declaration } = row.lines.find((line) => line.key === selectedRslKey);
      if (selectedFilteredRow.length > 1 && row.locked) return;

      if (!declaration?.locked) {
        if (selectedFilteredRow.length === 1) {
          selectedAttachments.push(...attachments);
          selectedSkuExemptions.push(...sku_exemptions);
          selectedDeclaration = declaration;
        }
        selectedProducts.push(row);
      }
    });

    if (selectedProducts.length < 1) return;
    let thread = null;
    if (selectedProducts.length === 1) {
      thread = this.getThread(selectedProducts[0].rid, selectedRslRid);
    }
    const dialogRef = this.dialog.open(ComplianceQuestionnaireEditComponent, {
      panelClass: 'dialog-panel',
      disableClose: true,
      closeOnNavigation: false,
      width: '800px',
      autoFocus: false,
      data: {
        editable,
        selectedRsl: { key: selectedRslKey, ...selectedRsl },
        selectedProducts,
        attachments: selectedAttachments ?? [],
        sku_exemptions: selectedSkuExemptions ?? [],
        declaration: selectedDeclaration ?? null,
        actions: this.response.tabs[0].actions,
        thread
      }
    });
    const instance = dialogRef.componentInstance;

    instance.submitDataEmitter
      .pipe(
        switchMap(({ payload, attachments, deletedAttachments, comment }) => {
          if (!payload) return of(null);
          if (comment) {
            payload = { ...payload, comment };
          }
          return this.informationRequestService.saveRequest(this.rid, payload).pipe(
            switchMap((success) => {
              const requests = [];
              if (deletedAttachments?.length) {
                requests.push(this.informationRequestService.deleteProductAttachment(this.rid, deletedAttachments));
              }

              if (attachments?.length) {
                requests.push(
                  this.informationRequestService.uploadRslAttachments(
                    this.rid,
                    selectedProducts.map((row) => row.rid),
                    attachments,
                    selectedRsl.rid
                  )
                );
              }
              if (!requests?.length) return of([success]);
              return forkJoin(requests);
            }),
            catchError((err) => {
              this.toastrService.showMessage(err.error.message, 5000);
              return of([]);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((success) => {
        if (success.length) {
          this.toastrService.showMessage('Saved Successfully', 1000);
          dialogRef?.close();
          this.reload();
        }
      });
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;
    this.gridReady.emit(this.gridApi);
    if (this.gridApi) {
      this.checkAcceptButton();
    }
  }

  sendComment(paramsComment?: any) {
    if (paramsComment.threadId) {
      this.addComment(paramsComment, paramsComment.threadId);
    } else {
      this.createNewThread(paramsComment);
    }
  }

  private createNewThread(paramsComment) {
    const payload = {
      message: { content: paramsComment?.comment },
      tags: [
        `sku:${paramsComment?.selectedProductId}`,
        `regulation:${paramsComment?.selectedRegulationId}`,
        `questionnaire:#${paramsComment.questionnaireId}`
      ]
    };

    this.threadApiService.createThread(payload).subscribe({
      next: () => {
        this.reload();
      }
    });
  }

  private addComment(paramsComment, threadId) {
    const commentContent = paramsComment.comment;
    if (!commentContent) {
      return;
    }
    const payload = { content: commentContent };
    this.threadApiService.addComment(payload, threadId).subscribe({
      next: () => {
        this.reload();
      }
    });
  }

  private createTableOverrides() {
    const { lines } = this.response.tabs[0];
    this.editable = this.response.actions.edit;
    this.reviewMode = this.response.actions.close && !this.isUserExternalSupplier;
    const rowData = this.getRowData(lines);
    this.generateGroupLabels(rowData);
    this.gridOverrides = {
      groupAllowUnbalanced: true, // stops creating "(Blanks)" group
      columnDefs: this.getColumns(lines[0], this.response.additional_data.rsls),
      rowData: rowData,
      defaultColDef: {
        minWidth: 100,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        filter: true,
        resizable: true
      },
      autoGroupColumnDef: {
        headerName: 'Statement',
        pinned: 'left',
        minWidth: 200,
        lockPosition: true,
        headerTooltip: 'Statement',
        valueFormatter: (params) => {
          if (params.node.group) {
            return this.groupLabelMap.get(params.value) || params.value;
          }
          return params.value || ''; // leaf rows stay blank
        },
        tooltipValueGetter: (params) => {
          if (params.node.group) {
            return this.groupLabelMap.get(params.value) || 'No Group';
          }
        },
        cellRendererParams: {
          suppressCount: true,
          checkbox: (params) => {
            return params.node.group === true;
          }
        }
      },
      groupDefaultExpanded: 1,
      rowSelection: RowSelection.MULTIPLE,
      groupSelectsChildren: true,
      suppressRowClickSelection: true,
      suppressAggFuncInHeader: false,
      pagination: true,
      paginationPageSize: 10,
      paginationAutoPageSize: false,
      paginationPageSizeSelector: [10, 50, 100],
      getRowId: (params: GetRowIdParams) => String(params.data.rid),
      getRowStyle: (params) => {
        if (params.data?.name && this.invalidProductNames.has(params.data.name)) {
          return { background: ColorAliases.ERROR_CONTAINER };
        }
        return {};
      }
    };
  }

  private getColumns(lines, rsls) {
    const columnDefs = [];
    // Dynamically find Product, Part Number and MPN columns from lines array
    const productColumn = lines.find((line) => line.key === 'name');
    const mpnColumn = lines.find((line) => line.key === 'prop_MPN');
    const partNumberColumn = lines.find((line) => line.key === 'prop_PN');
    if (this.groupColumnVisible && this.reviewMode) {
      columnDefs.push({
        field: 'submission_id',
        rowGroup: true,
        hide: true,
        lockPosition: true,
        pinned: true,
        valueFormatter: (params) => {
          return this.groupLabelMap.get(params.value) || params.value;
        }
      });
    }

    // Add Product column if found
    if (productColumn) {
      columnDefs.push({
        headerName: productColumn.label || 'Product',
        field: productColumn.key,
        minWidth: 200,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        tooltipField: productColumn.key,
        headerTooltip: productColumn.label || 'Product',
        headerCheckboxSelection: (params) => this.editable || this.reviewMode,
        checkboxSelection: (params) => (this.editable || this.reviewMode) && !params.node.group,
        lockPosition: true,
        pinned: 'left',
        valueGetter: (params) => (params.node.group ? null : params.data[productColumn.key]),
        cellRenderer: GridCellFileUploadComponent,
        cellRendererParams: (params: ICellRendererParams) => {
          if (params.node.group) {
            return {};
          }
          return {
            onUpload: (params: ICellRendererParams, event) => {
              if (!event.target.files.length && !params?.data?.rid && !this.rid) return;
              this.informationRequestService
                .uploadRslAttachments(
                  this.rid,
                  [params?.data?.rid],
                  event.target.files,
                  null,
                  QUESTIONNAIRE_IPC_FILE_TYPE
                )
                .subscribe({
                  next: (res: { product: any }) => {
                    const rowData = this.getRowData([res.product])[0];
                    const rowNode = params.api.getRowNode(params.data.rid);
                    rowNode.setData({ ...rowNode.data, ...rowData });
                    params.api.refreshCells({ rowNodes: [rowNode], force: true });
                    this.toastrService.show('Uploaded Successfully');
                  }
                });
            },
            onRemove: (params: ICellRendererParams) => {
              if (!params?.data?.rid && !this.rid) return;
              this.informationRequestService
                .deleteProductAttachment(this.rid, [params?.data?.rid], QUESTIONNAIRE_IPC_FILE_TYPE)
                .subscribe({
                  next: (res) => {
                    const rowNode: any = params.api.getRowNode(params.data.rid);
                    if (!rowNode) return;
                    rowNode.setData({
                      ...rowNode.data,
                      ipc_file_attached: false,
                      ipc_attachment: null
                    });
                    this.toastrService.show('Deleted Successfully');
                  }
                });
            },
            onDownload: (params: ICellRendererParams) => {
              this.informationRequestService.onAttachmentDownload(params?.data?.ipc_attachment?.link ?? '');
            },
            editable: this.editable && !params.data.locked,
            info: 'Adding IPC FMD file will update compliance status in all columns',
            text: 'Add IPC'
          };
        }
      });
    }

    // Add MPN column if found
    if (mpnColumn) {
      columnDefs.push({
        headerName: mpnColumn.label || 'MPN',
        field: mpnColumn.key,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        tooltipField: mpnColumn.key,
        headerTooltip: mpnColumn.label || 'MPN',
        lockPosition: true,
        pinned: 'left',
        minWidth: 150,
        valueGetter: (params) => (params.node.group ? null : params.data[mpnColumn.key])
      });
    }

    // Add Part Number column if found
    if (partNumberColumn) {
      columnDefs.push({
        headerName: partNumberColumn.label || 'Part Number',
        field: partNumberColumn.key,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        tooltipField: partNumberColumn.key,
        headerTooltip: partNumberColumn.label || 'Part Number',
        lockPosition: true,
        pinned: 'left',
        minWidth: 150,
        valueGetter: (params) => (params.node.group ? null : params.data[partNumberColumn.key])
      });
    }

    // Group dynamic fields by group_name
    const groupedColumns: { [groupName: string]: any[] } = {};

    lines.forEach((line) => {
      if (['name', 'status', 'prop_MPN', 'prop_PN', 'submission_id'].includes(line.key)) return;
      const rslRid = rsls.find((rls) => rls.name === line.label).rid ?? '';
      const column = {
        headerName: line.label,
        field: line.key,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        tooltipField: line.key,
        headerTooltip: line.label,
        width: 300,
        minWidth: 300,
        valueGetter: (params) => (params.node.group ? null : params.data[line.key]),
        headerComponent: GridHeaderWithButtonComponent,
        headerComponentParams: {
          rid: rslRid,
          editable: this.editable,
          onClick: (params) => this.onEdit(params, this.editable, true),
          filteredRowSelection$: this.filteredRowSelection$
        },
        cellRendererSelector: (params) => {
          if (params.node.group) {
            return null;
          }
          return {
            component: GridCellWithButtonComponent
          };
        },
        cellRendererParams: (params) => {
          if (params.node.group) {
            return {};
          }
          const thread = this.getThread(params.data.rid, rslRid);
          const statement = params.data.lines.find((line) => line.key === `out_l_declaration_${rslRid}`);
          const data: any = {
            placeholder: 'Select Compliance',
            rid: rslRid,
            locked: statement.declaration?.locked,
            editable: (this.editable && !params.data.locked) || (this.reviewMode && !params.data.locked),
            attachments: statement.attachments.map((file) => file.original_name).join(' '),
            attachmentsCount: statement.attachments?.length || 0,
            infoTooltip: statement.declaration?.is_prefilled
              ? 'Pre-filled statement\nThis data point was pre-filled by the system and may be inaccurate. Please review it carefully.'
              : undefined,
            onClick: () => false,
            showEditIcon: true
          };
          if (thread) {
            data.messageCount = thread?.unseen_count;
          }
          return data;
        },
        onCellClicked: (params) => {
          if (params.node.group) {
            return {};
          }
          if (this.reviewMode) {
            params.node.setSelected(true, true);
            this.onReview(params, this.reviewMode);
          } else {
            const statement = params.data.lines.find((line) => line.key === `out_l_declaration_${rslRid}`);
            if (!this.editable && !statement.declaration) return;
            const editable =
              this.editable && !params.data.locked && !params.data?.ipc_file_attached && !statement.declaration?.locked;
            params.node.setSelected(true, true);
            this.onEdit(params, editable);
          }
        }
      };

      if (line.group_name) {
        if (!groupedColumns[line.group_name]) {
          groupedColumns[line.group_name] = [];
        }
        groupedColumns[line.group_name].push(column);
      } else {
        columnDefs.push(column);
      }
    });

    // Add grouped columns to the columnDefs

    for (const [groupName, columns] of Object.entries(groupedColumns)) {
      columnDefs.push({
        headerName: groupName.toUpperCase(),
        headerGroupComponent: GridHeaderGroupComponent,
        headerGroupComponentParams: { icon: MdsIcon.COMPLIANCE },
        children: columns
      });
    }
    if (this.reviewMode) {
      columnDefs.push({
        headerName: 'Status',
        field: 'status',
        pinned: 'left',
        minWidth: 220,
        width: 220,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        lockPosition: true,
        headerComponent: GridHeaderWithButtonComponent,
        cellRendererSelector: (params) => {
          if (params.node.group) {
            return null;
          }
          return {
            component: GridSelectRendererComponent
          };
        },
        headerClass: 'custom-header-class',
        onCellValueChanged: (params) => (params.node.group || this.isBulkReview ? null : this.onStatusUpdate(params)),
        headerComponentParams: {
          btnText: 'Bulk Review',
          editable: this.reviewMode,
          onClick: () => this.onBulkReview(),
          filteredRowSelection$: this.filteredRowSelection$
        }
      });
    }
    return columnDefs;
  }

  private getRowData(lines) {
    const rowData = [];

    lines.forEach((productLines) => {
      const row: Record<string, any> = {};
      productLines.forEach((line) => {
        row[line.key] = line.value || null;
        if (this.reviewMode && line.key.includes('out_l_declaration_') && line?.declaration?.submission_id) {
          row.submission_id = line?.declaration?.submission_id;
          row.statement = line?.declaration?.statement;
        }
        if (line?.declaration?.review_status && this.reviewMode) {
          row.status = line?.declaration?.review_status === 'reopened' ? 'Reject' : 'Approve';
        }
      });
      row.lines = productLines;
      rowData.push({ ...row, ...productLines[0] });
    });
    return rowData;
  }

  private reload() {
    this.eventDelegatorService.emitter('custom-ngx-event', {
      type: 'toolbar-reload-event',
      action: 'reload',
      controller: window.location.pathname
    });
  }

  private updateGridRowStatus(gridParams: any, status: string): void {
    const skuRids = [gridParams.data?.rid.replace('#', '')];
    this.submitStatus(this.buildStatusPayload(skuRids, gridParams.newValue));
    const saved = this.globalDataService.getStatusNodeData() || [];
    const filtered = saved.filter((item) => item.node.id !== gridParams.node.id);
    filtered.push({ node: gridParams.node, status });
    this.globalDataService.setStatusNodeData(filtered);
    gridParams.node.setDataValue('status', status);
  }

  private buildStatusPayload(skuRids: string[], status: string, comment?: string) {
    return {
      sku_rid: skuRids,
      review_status: status === 'Reject' ? 'reopened' : 'closed',
      ...(comment ? { comment } : {})
    };
  }

  submitStatus(payload) {
    this.informationRequestService
      .saveStatusUpdates(this.rid, payload)
      .pipe(
        finalize(() => {
          this.isBulkReview = false;
        })
      )
      .subscribe(() => {
        if (payload?.sku_rid?.length > 1) {
          this.reload();
        }
      });
  }

  private getThread(skuId, regulationId) {
    return this.threads?.find(
      (thread) => thread.metadata.sku_id === skuId && thread.metadata.regulation_id === regulationId
    );
  }

  async getFilterDataRows(): Promise<any[]> {
    const selectedFilteredRows = [];
    this.gridApi.forEachNodeAfterFilter((node: any) => {
      if (!node.group && node.displayed && node.selected) {
        selectedFilteredRows.push(node.data);
      }
    });
    return selectedFilteredRows;
  }

  async updateBulkAction() {
    const selectedFilteredRows = await this.getFilterDataRows();
    this.filteredRowSelection$.next(selectedFilteredRows);
  }

  onSwitchView(selectedTab: { label: string; value: CampaignTabsEnum }): void {
    this.selectedTab = selectedTab.value;
  }
}
